<template>
  <add-image  v-if="imageForActions === '' && imageResult === ''">
    <div style="text-align: center; margin-bottom: 12px; color: var(--secondary-100);">{{ $t('menu.outpaint') }}</div>
    <img :src="`${baseUrl}/images/multi-menu/outpaint.png`" alt="">
  </add-image>
  <div
    v-else-if="imageForActions !== '' && imageResult === ''"
    class="service__container outpaint"
    :class="{ 'service__container--mobile': isMobile }"
  >
    <div class="outpaint-edit">
      <image-finetune
        class="outpaint-edit__finetune"
        v-if="isEdit"
        :src="imageForActions"
        @reset="isEdit = false"
        @save="editHandler"
        :actions="['reset', 'save']"
      />
      <template v-else>
      <div class="outpaint-edit__settings">
        <div class="outpaint-edit__settings__title">
          {{ $t('common.canvas-size') }}
        </div>
        <div class="outpaint-edit__settings__content">
          <div class="outpaint-edit__settings__sizes">
            <ai-clear-button
              v-for="(size, index) in sizesList"
              :key="index"
              class="outpaint-edit__settings__sizes__item"
              :class="{ 'outpaint-edit__settings__sizes__item--active': sizeIndex === index }"
              @click="setSize(index)"
            >
              <div class="outpaint-edit__settings__sizes__item__icon">
                <inline-svg
                  v-if="size.width > size.height"
                  class="fill"
                  :src="require('@/assets/icons/sizes/horisontal.svg')"
                />
                <inline-svg
                  v-else-if="size.width < size.height"
                  class="fill"
                  :src="require('@/assets/icons/sizes/vertical.svg')"
                />
                <inline-svg v-else class="fill" :src="require('@/assets/icons/sizes/square.svg')" />
              </div>
              <div class="outpaint-edit__settings__sizes__item__text">{{ size.name }}</div>
              <v-spacer />
              <div class="outpaint-edit__settings__sizes__item__value">
                {{ size.width }}
                <div class="outpaint-edit__divider">×</div>
                {{ size.height }}
              </div>
            </ai-clear-button>
            <ai-clear-button
              class="outpaint-edit__settings__sizes__item"
              :class="{ 'outpaint-edit__settings__sizes__item--active': sizeIndex === -1 }"
              @click="setSize(-1)"
            >
              <div style="position: relative; width: 100%" class="d-flex align-center">
                <div class="outpaint-edit__settings__sizes__item__icon">
                  <inline-svg class="stroke" :src="require('@/assets/icons/sizes/custom.svg')" />
                </div>
                <div class="outpaint-edit__settings__sizes__item__text">{{ $t('editor.settings.custom') }}</div>
                <v-spacer />
                <div class="outpaint-edit__settings__sizes__item__value">
                  <v-text-field
                    v-model.number.lazy="customWidth"
                    class="outpaint-edit__input"
                    hide-details
                    height="18"
                  />
                  <div class="outpaint-edit__divider">×</div>
                  <v-text-field v-model.number.lazy="customHeight" class="outpaint-edit__input" hide-details />
                </div>
              </div>
            </ai-clear-button>
          </div>
          <ai-button
            v-if="isMobile"
            :disabled="generationLoading"
            theme="gradient-primary"
            @click="outpaintFileHandler"
            class="outpaint-edit__settings__button"
          >
            <template v-if="!generationLoading">
              {{ $t('outpaint.outpaint') }}
            </template>
            <time-counter v-else />
          </ai-button>
        </div>
      </div>
      <div class="outpaint-edit__workarea">
        <ai-card :radius="16" class="outpaint-edit__workarea__card">
          <div class="outpaint-edit__canvas">
            <div class="outpaint-edit__canvas-box">
              <ai-sceleton v-if="loading" class="outpaint-edit__canvas__sceleton" />
              <div class="outpaint-edit__canvas__zoom">
                <ai-clear-button
                  class="outpaint-edit__canvas__zoom__button"
                  :active-zoom="activeZoom"
                  @click="zoomOutHandler"
                >
                  <inline-svg :src="require('@/assets/icons/IconDecrease.svg')" />
                </ai-clear-button>
                <ai-clear-button class="outpaint-edit__canvas__zoom__button" @click="zoomInHandler">
                  <inline-svg :src="require('@/assets/icons/IconIncrease.svg')" />
                </ai-clear-button>
              </div>
              <ai-canvas
                v-if="!loading"
                :key="key"
                :active-zoom="activeZoom"
                :images="imageData"
                :width="canvasWidth"
                :height="canvasHeight"
                :show-controls="false"
                ref="aiCanvasRef"
                @active-zoom-set="activeZoomSetHandler"
                @canvas-update="canvasUpdateHandler"
              />
              <ai-drop-loader v-if="generationLoading" />
            </div>
          </div>
          <div class="outpaint-edit__workarea__data">
            <!-- <div class="outpaint-edit__workarea__data__info">
              {{ imageWidth }} × {{ imageHeight }}px | {{ (imageSize / 1024).toFixed(2) }} kb
            </div> -->
            <v-spacer />
            <div class="outpaint-edit__workarea__actions">
              <ai-button
                theme="dark"
                @click="
                  imageForActions = '';
                  imageResult = '';
                "
                >{{ $t('services.reset') }}</ai-button
              >
              <ai-button
                v-if="!isMobile"
                :disabled="generationLoading"
                theme="gradient-primary"
                class="outpaint-edit__workarea__button"
                @click="outpaintFileHandler"
              >
                <template v-if="!generationLoading">
                  {{ $t('outpaint.outpaint') }}
                </template>
                <time-counter v-else />
              </ai-button>
            </div>
          </div>
        </ai-card>
      </div>
      </template>
    </div>
  </div>
  <result-image v-else-if="imageForActions !== '' && imageResult !== ''" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useEventBus } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import { useCommonStore } from '@/store/common';
import { useServicesStore } from '@/store/services';

import { removeBase64Prefix } from '@/helpers/removeBase64Prefix';

import { ICanvasImage } from '@/types/ICanvasImage';

import AiButton from '@/components/UI/AiButton/AiButton.vue';
import AiCanvas from '@/components/UI/AiCanvas/AiCanvas.vue';
import AiCard from '@/components/UI/AiCard/AiCard.vue';
import AiClearButton from '@/components/UI/AiClearButton/AiClearButton.vue';
import AiDropLoader from '@/components/UI/AiDropLoader/AiDropLoader.vue';
import AiSceleton from '@/components/UI/AiSceleton/AiSceleton.vue';

import AddImage from '@/components/common/AddImage/AddImage.vue';
import ImageFinetune from '@/components/common/ImageFinetune/ImageFinetune.vue';
import ResultImage from '@/components/common/ResultImage/ResultImage.vue';
import TimeCounter from '@/components/common/TimeCounter/TimeCounter.vue';

import { sizesEn } from '@/data/outpaintSizes/sizesEn';

interface ICanvasData {
  activeProductId?: string;
  activeProductUid?: string;
  image: string;
  mask: string;
  products: ICanvasImage[];
}

interface ImageData {
  id: string;
  image: string;
  mask: boolean;
}

const { t } = useI18n();

const baseUrl = process.env.VUE_APP_BASE_URL;

const busError = useEventBus<string>('toast-error');

const { generateImage } = useServicesStore();

const { isMobile } = storeToRefs(useCommonStore());
const { imageForActions, imageResult, imagePrev } = storeToRefs(useServicesStore());

const key = ref<number>(0);
const loading = ref<boolean>(false);
const generationLoading = ref<boolean>(false);
const sizeIndex = ref<number>(0);
const customWidth = ref<number>(2048);
const customHeight = ref<number>(2048);
const imageWidth = ref<number>(0);
const imageHeight = ref<number>(0);
const activeZoom = ref<number>(1);
const imageData = ref<ImageData[]>([]);
const canvasWidth = ref<number>(600);
const canvasHeight = ref<number>(600);
const aiCanvasRef = ref<any>(null);
const canvasData = ref<ICanvasData>({
  image: '',
  mask: '',
  products: [],
});
const isEdit = ref<boolean>(false);

const sizesList = computed(() => (sizesEn));

const outpaintFileHandler = (): void => {
  generationLoading.value = true;
  if (aiCanvasRef.value) {
    aiCanvasRef.value.canvasUpdateData();
    generateImage({
      action: 'outpaint',
      image: removeBase64Prefix(imageForActions.value),
      params: {
        size: {
          height: canvasHeight.value,
          width: canvasWidth.value,
        },
        transform: canvasData.value.products[0].transform,
      },
    })
      .then(async (res) => {
        const bg = await aiCanvasRef.value.getBackground();
        imagePrev.value = imageForActions.value;
        imageForActions.value = bg.enlargedBase64src;
        imageResult.value = `data:image/jpeg;base64,${res.image}`;
      })
      .catch((e) => {
        busError.emit(e?.error || t('common.err-gen'));
      })
      .finally(() => {
        generationLoading.value = false;
      });
  }
};

const zoomInHandler = (): void => {
  activeZoom.value += 0.1;
};

const zoomOutHandler = (): void => {
  activeZoom.value -= 0.1;
};

const activeZoomSetHandler = (newVal: number): void => {
  activeZoom.value = newVal;
};

const setSize = (index: number): void => {
  sizeIndex.value = index;
  if (index === -1) {
    canvasWidth.value = customWidth.value;
    canvasHeight.value = customHeight.value;
  } else {
    canvasWidth.value = sizesList.value[index].width;
    canvasHeight.value = sizesList.value[index].height;
  }
};

const canvasUpdateHandler = (data: any): void => {
  canvasData.value = data;
};

const imageSizeHandler = (): void => {
  if (imageForActions.value !== '') {
    const img = new Image();
    img.onload = () => {
      imageWidth.value = img.width;
      imageHeight.value = img.height;
    };
    img.src = imageForActions.value;
    key.value += 1;
  }
};

const editHandler = (image: string): void => {
  imageForActions.value = image;
  isEdit.value = false;
};

onMounted(() => {
  if (imageForActions.value !== '') {
    imageData.value = [
      {
        id: 'outpaint',
        image: imageForActions.value,
        mask: false,
      },
    ];
  }
  imageSizeHandler();
  setSize(0);
});

watch(imageForActions, () => {
  if (imageForActions.value !== '') {
    imageData.value = [
      {
        id: 'outpaint',
        image: imageForActions.value,
        mask: false,
      },
    ];
  }
  imageSizeHandler();
});

watch(imageResult, () => {
  if (imageResult.value === '' && imagePrev.value !== '') {
    imageForActions.value = imagePrev.value;
  }

  if (imageResult.value === '') {
    if (imageForActions.value !== '') {
      imageData.value = [
        {
          id: 'outpaint',
          image: imageForActions.value,
          mask: false,
        },
      ];
    }
  }
});

watch(customHeight, (newVal) => {
  canvasHeight.value = newVal;
});

watch(customWidth, (newVal) => {
  canvasWidth.value = newVal;
});
</script>

<style scoped lang="scss" src="./Outpaint.scss" />
